import type { WsConnectionState } from '@talkvibe/shared';

/**
 * The parts of a realtime connection that have nothing to do with what it
 * carries: getting a ticket, opening the socket, surviving a laptop lid or a
 * train tunnel, and coming back without stampeding the server.
 *
 * Both sockets this app opens — the conversation socket and the user channel —
 * need exactly this, and needed it identically. Keeping two copies would mean
 * every reconnection bug has to be found and fixed twice, which is how the two
 * quietly stop behaving the same. What differs between them is the protocol,
 * and that is what subclasses supply.
 */

const HEARTBEAT_INTERVAL_MS = 25_000;
const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 30_000;

/**
 * How long a socket has to stay open before the attempt counter is forgiven.
 *
 * Opening is not the same as working. A connection that opens and is closed
 * again a moment later — by a proxy that will not hold it, by a server that
 * rejects what comes next — used to reset the counter on `open`, so the very
 * next failure backed off by 500ms and the one after that by 500ms again. The
 * result was a client reconnecting about once a second forever, and a request
 * for a fresh ticket every time, which is what it looked like in the server
 * log: `POST /api/realtime/user-ticket` on a loop with no end.
 *
 * Five seconds is longer than any handshake and shorter than any real session.
 */
const STABLE_AFTER_MS = 5_000;
/** Normal closure or "going away" — not an error worth retrying loudly. */
const CLEAN_CLOSE_CODES = new Set([1000, 1001]);
/** Bounds the queue so a long outage cannot grow it without limit. */
const MAX_OUTBOX = 50;

export abstract class ReconnectingSocket<ClientFrame> {
  private socket: WebSocket | null = null;
  private state: WsConnectionState = 'disconnected';
  private attempt = 0;
  /** Running while a fresh connection is still proving it will hold. */
  private stableTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  /** Set when the caller asked to disconnect, to stop the retry loop. */
  private closedByUser = false;
  private readonly outbox: ClientFrame[] = [];

  constructor(private readonly onStateChange: (state: WsConnectionState) => void) {}

  get connectionState(): WsConnectionState {
    return this.state;
  }

  /* ------------------------------------------------------------------ */
  /* Supplied by the protocol                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Fetches a fresh socket URL. Every attempt asks again, because tickets are
   * deliberately short-lived and the one used an hour ago is long expired.
   */
  protected abstract ticketUrl(): Promise<string>;

  /** Handles one decoded inbound frame. */
  protected abstract handleFrame(frame: unknown): void;

  /** The keep-alive to send while idle. */
  protected abstract heartbeat(): ClientFrame;

  /**
   * Whether a frame is worth holding while the socket is down. The default
   * keeps everything; ephemeral signals should say no — a stale "is typing"
   * delivered a minute later is worse than none at all.
   */
  protected shouldQueue(_frame: ClientFrame): boolean {
    return true;
  }

  /** Called once each time the socket opens, after the outbox is flushed. */
  protected onOpen(): void {
    // Intentionally empty.
  }

  /* ------------------------------------------------------------------ */

  async connect(): Promise<void> {
    if (this.socket || this.closedByUser) return;
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let url: string;
    try {
      url = await this.ticketUrl();
    } catch {
      // Could not even get a ticket — the API is down or we are signed out.
      this.scheduleReconnect();
      return;
    }

    // The caller may have disconnected while the ticket was in flight.
    if (this.closedByUser) return;

    try {
      this.socket = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket.addEventListener('open', () => {
      this.setState('connected');
      this.startHeartbeat();
      this.flushOutbox();
      this.onOpen();

      // Forgiven only once it has held. See STABLE_AFTER_MS.
      this.clearStableTimer();
      this.stableTimer = window.setTimeout(() => {
        this.stableTimer = null;
        this.attempt = 0;
      }, STABLE_AFTER_MS);
    });

    this.socket.addEventListener('message', (event) => {
      let frame: unknown;
      try {
        frame = JSON.parse(String(event.data));
      } catch {
        return;
      }
      this.handleFrame(frame);
    });

    this.socket.addEventListener('close', (event) => {
      this.clearStableTimer();
      this.teardownSocket();
      if (this.closedByUser) {
        this.setState('disconnected');
        return;
      }
      this.setState(CLEAN_CLOSE_CODES.has(event.code) ? 'disconnected' : 'error');
      this.scheduleReconnect();
    });

    this.socket.addEventListener('error', () => {
      // 'close' always follows, and that is where reconnection is decided.
      this.setState('error');
    });
  }

  /** Sends a frame, queueing it if the socket is not open. */
  send(frame: ClientFrame): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(frame));
      return;
    }
    if (!this.shouldQueue(frame)) return;
    if (this.outbox.length < MAX_OUTBOX) this.outbox.push(frame);
  }

  disconnect(): void {
    this.closedByUser = true;
    this.clearTimers();
    this.outbox.length = 0;
    if (this.socket) {
      try {
        this.socket.close(1000, 'Client disconnected');
      } catch {
        // Already closed.
      }
    }
    this.teardownSocket();
    this.setState('disconnected');
  }

  /* ------------------------------------------------------------------ */

  private flushOutbox(): void {
    const pending = this.outbox.splice(0, this.outbox.length);
    for (const frame of pending) this.send(frame);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    // Keeps intermediaries from closing an idle connection, and surfaces a
    // half-open socket that would otherwise look connected forever.
    this.heartbeatTimer = window.setInterval(() => {
      this.send(this.heartbeat());
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByUser || this.reconnectTimer !== null) return;

    this.attempt += 1;
    const backoff = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** (this.attempt - 1));
    // Jitter over the second half of the window: it still spreads a thundering
    // herd, and it cannot round down to "immediately" the way full jitter can.
    const delay = backoff / 2 + Math.random() * (backoff / 2);

    this.setState('reconnecting');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) {
      window.clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  private teardownSocket(): void {
    this.stopHeartbeat();
    this.socket = null;
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    this.clearStableTimer();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(state: WsConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange(state);
  }
}
